import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, endImpersonation } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [impersonation, setImpersonation] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setCompany(data.company || null);
      setImpersonation(data.impersonation || null);
      return data.user;
    } catch (err) {
      // Expected on first load when there's no session - clear state silently.
      setUser(null);
      setCompany(null);
      setImpersonation(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth, AuthCallback handles the exchange.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      await fetchMe();
      setLoading(false);
    })();
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network failure during logout shouldn't block the user — clear local state anyway.
      console.warn("logout request failed", err);
    }
    endImpersonation();
    setToken(null);
    setUser(null);
    setCompany(null);
    setImpersonation(null);
  }, []);

  return (
    <AuthCtx.Provider
      value={{
        user,
        company,
        impersonation,
        loading,
        refresh: fetchMe,
        setUser,
        setCompany,
        logout,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
}
